import {
    AppwriteException,
    Client,
    type Payload,
    UploadProgress,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { Framework } from '../enums/framework';
import { BuildRuntime } from '../enums/build-runtime';
import { Adapter } from '../enums/adapter';
import { ProjectKeyScopes } from '../enums/project-key-scopes';
import { SiteTemplateUseCase } from '../enums/site-template-use-case';
import { TemplateReferenceType } from '../enums/template-reference-type';
import { VCSReferenceType } from '../enums/vcs-reference-type';
import { DeploymentDownloadType } from '../enums/deployment-download-type';
export class Sites {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Get a list of all the project's sites. You can use the query params to filter your results.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, enabled, framework, deploymentId, buildCommand, installCommand, outputDirectory, installationId
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SiteList>}
     */
    list(params?: {
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.SiteList>;
    /**
     * Get a list of all the project's sites. You can use the query params to filter your results.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, enabled, framework, deploymentId, buildCommand, installCommand, outputDirectory, installationId
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SiteList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    list(
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.SiteList>;
    list(
        paramsOrFirst?:
            { queries?: string[]; search?: string; total?: boolean } | string[],
        ...rest: [string?, boolean?]
    ): Promise<Models.SiteList> {
        let params: { queries?: string[]; search?: string; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                search: rest[0] as string,
                total: rest[1] as boolean,
            };
        }

        const queries = params.queries;
        const search = params.search;
        const total = params.total;

        const apiPath = '/sites';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'siteList',
            models: {
                siteList: {
                    defaults: {
                        total: 0,
                        sites: [],
                    },
                    properties: {
                        sites: {
                            items: 'site',
                        },
                    },
                },
                site: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        enabled: true,
                        live: true,
                        logging: true,
                        framework: '',
                        deploymentRetention: 0,
                        deploymentId: '',
                        deploymentCreatedAt: '',
                        deploymentScreenshotLight: '',
                        deploymentScreenshotDark: '',
                        latestDeploymentId: '',
                        latestDeploymentCreatedAt: '',
                        latestDeploymentStatus: '',
                        scopes: [],
                        vars: [],
                        timeout: 15,
                        installCommand: '',
                        buildCommand: '',
                        startCommand: '',
                        outputDirectory: '',
                        installationId: '',
                        providerRepositoryId: '',
                        providerBranch: '',
                        providerRootDirectory: '',
                        providerSilentMode: false,
                        providerBranches: [],
                        providerPaths: [],
                        buildSpecification: 's-1vcpu-512mb',
                        runtimeSpecification: 's-1vcpu-512mb',
                        buildRuntime: '',
                    },
                    properties: {
                        vars: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new site.
     *
     * @param {string} params.siteId - Site ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Site name. Max length: 128 chars.
     * @param {Framework} params.framework - Sites framework.
     * @param {BuildRuntime} params.buildRuntime - Runtime to use during build step.
     * @param {boolean} params.enabled - Is site enabled? When set to 'disabled', users cannot access the site but Server SDKs with and API key can still access the site. No data is lost when this is toggled.
     * @param {boolean} params.logging - When disabled, request logs will exclude logs and errors, and site responses will be slightly faster.
     * @param {number} params.timeout - Maximum request time in seconds.
     * @param {string} params.installCommand - Install Command.
     * @param {string} params.buildCommand - Build Command.
     * @param {string} params.startCommand - Custom start command. Leave empty to use default.
     * @param {string} params.outputDirectory - Output Directory for site.
     * @param {Adapter} params.adapter - Framework adapter defining rendering strategy. Allowed values are: static, ssr
     * @param {string} params.installationId - Appwrite Installation ID for VCS (Version Control System) deployment.
     * @param {string} params.fallbackFile - Fallback file for single page application sites.
     * @param {string} params.providerRepositoryId - Repository ID of the repo linked to the site.
     * @param {string} params.providerBranch - Production branch for the repo linked to the site.
     * @param {boolean} params.providerSilentMode - Is the VCS (Version Control System) connection in silent mode for the repo linked to the site? In silent mode, comments will not be made on commits and pull requests.
     * @param {string} params.providerRootDirectory - Path to site code in the linked repo.
     * @param {string[]} params.providerBranches - List of branch name patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all branches.
     * @param {string[]} params.providerPaths - List of file path patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all file changes.
     * @param {string} params.buildSpecification - Build specification for the site deployments.
     * @param {string} params.runtimeSpecification - Runtime specification for the SSR executions.
     * @param {number} params.deploymentRetention - Days to keep non-active deployments before deletion. Value 0 means all deployments will be kept.
     * @param {ProjectKeyScopes[]} params.scopes - List of scopes allowed for API key auto-generated for every site build and SSR execution. Maximum of 200 scopes are allowed.
     * @param {string} params.description - Optional description for the site.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     */
    create(params: {
        siteId: string;
        name: string;
        framework: Framework;
        buildRuntime: BuildRuntime;
        enabled?: boolean;
        logging?: boolean;
        timeout?: number;
        installCommand?: string;
        buildCommand?: string;
        startCommand?: string;
        outputDirectory?: string;
        adapter?: Adapter;
        installationId?: string;
        fallbackFile?: string;
        providerRepositoryId?: string;
        providerBranch?: string;
        providerSilentMode?: boolean;
        providerRootDirectory?: string;
        providerBranches?: string[];
        providerPaths?: string[];
        buildSpecification?: string;
        runtimeSpecification?: string;
        deploymentRetention?: number;
        scopes?: ProjectKeyScopes[];
        description?: string;
    }): Promise<Models.Site>;
    /**
     * Create a new site.
     *
     * @param {string} siteId - Site ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Site name. Max length: 128 chars.
     * @param {Framework} framework - Sites framework.
     * @param {BuildRuntime} buildRuntime - Runtime to use during build step.
     * @param {boolean} enabled - Is site enabled? When set to 'disabled', users cannot access the site but Server SDKs with and API key can still access the site. No data is lost when this is toggled.
     * @param {boolean} logging - When disabled, request logs will exclude logs and errors, and site responses will be slightly faster.
     * @param {number} timeout - Maximum request time in seconds.
     * @param {string} installCommand - Install Command.
     * @param {string} buildCommand - Build Command.
     * @param {string} startCommand - Custom start command. Leave empty to use default.
     * @param {string} outputDirectory - Output Directory for site.
     * @param {Adapter} adapter - Framework adapter defining rendering strategy. Allowed values are: static, ssr
     * @param {string} installationId - Appwrite Installation ID for VCS (Version Control System) deployment.
     * @param {string} fallbackFile - Fallback file for single page application sites.
     * @param {string} providerRepositoryId - Repository ID of the repo linked to the site.
     * @param {string} providerBranch - Production branch for the repo linked to the site.
     * @param {boolean} providerSilentMode - Is the VCS (Version Control System) connection in silent mode for the repo linked to the site? In silent mode, comments will not be made on commits and pull requests.
     * @param {string} providerRootDirectory - Path to site code in the linked repo.
     * @param {string[]} providerBranches - List of branch name patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all branches.
     * @param {string[]} providerPaths - List of file path patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all file changes.
     * @param {string} buildSpecification - Build specification for the site deployments.
     * @param {string} runtimeSpecification - Runtime specification for the SSR executions.
     * @param {number} deploymentRetention - Days to keep non-active deployments before deletion. Value 0 means all deployments will be kept.
     * @param {ProjectKeyScopes[]} scopes - List of scopes allowed for API key auto-generated for every site build and SSR execution. Maximum of 200 scopes are allowed.
     * @param {string} description - Optional description for the site.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    create(
        siteId: string,
        name: string,
        framework: Framework,
        buildRuntime: BuildRuntime,
        enabled?: boolean,
        logging?: boolean,
        timeout?: number,
        installCommand?: string,
        buildCommand?: string,
        startCommand?: string,
        outputDirectory?: string,
        adapter?: Adapter,
        installationId?: string,
        fallbackFile?: string,
        providerRepositoryId?: string,
        providerBranch?: string,
        providerSilentMode?: boolean,
        providerRootDirectory?: string,
        providerBranches?: string[],
        providerPaths?: string[],
        buildSpecification?: string,
        runtimeSpecification?: string,
        deploymentRetention?: number,
        scopes?: ProjectKeyScopes[],
        description?: string,
    ): Promise<Models.Site>;
    create(
        paramsOrFirst:
            | {
                  siteId: string;
                  name: string;
                  framework: Framework;
                  buildRuntime: BuildRuntime;
                  enabled?: boolean;
                  logging?: boolean;
                  timeout?: number;
                  installCommand?: string;
                  buildCommand?: string;
                  startCommand?: string;
                  outputDirectory?: string;
                  adapter?: Adapter;
                  installationId?: string;
                  fallbackFile?: string;
                  providerRepositoryId?: string;
                  providerBranch?: string;
                  providerSilentMode?: boolean;
                  providerRootDirectory?: string;
                  providerBranches?: string[];
                  providerPaths?: string[];
                  buildSpecification?: string;
                  runtimeSpecification?: string;
                  deploymentRetention?: number;
                  scopes?: ProjectKeyScopes[];
                  description?: string;
              }
            | string,
        ...rest: [
            string?,
            Framework?,
            BuildRuntime?,
            boolean?,
            boolean?,
            number?,
            string?,
            string?,
            string?,
            string?,
            Adapter?,
            string?,
            string?,
            string?,
            string?,
            boolean?,
            string?,
            string[]?,
            string[]?,
            string?,
            string?,
            number?,
            ProjectKeyScopes[]?,
            string?,
        ]
    ): Promise<Models.Site> {
        let params: {
            siteId: string;
            name: string;
            framework: Framework;
            buildRuntime: BuildRuntime;
            enabled?: boolean;
            logging?: boolean;
            timeout?: number;
            installCommand?: string;
            buildCommand?: string;
            startCommand?: string;
            outputDirectory?: string;
            adapter?: Adapter;
            installationId?: string;
            fallbackFile?: string;
            providerRepositoryId?: string;
            providerBranch?: string;
            providerSilentMode?: boolean;
            providerRootDirectory?: string;
            providerBranches?: string[];
            providerPaths?: string[];
            buildSpecification?: string;
            runtimeSpecification?: string;
            deploymentRetention?: number;
            scopes?: ProjectKeyScopes[];
            description?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                name: string;
                framework: Framework;
                buildRuntime: BuildRuntime;
                enabled?: boolean;
                logging?: boolean;
                timeout?: number;
                installCommand?: string;
                buildCommand?: string;
                startCommand?: string;
                outputDirectory?: string;
                adapter?: Adapter;
                installationId?: string;
                fallbackFile?: string;
                providerRepositoryId?: string;
                providerBranch?: string;
                providerSilentMode?: boolean;
                providerRootDirectory?: string;
                providerBranches?: string[];
                providerPaths?: string[];
                buildSpecification?: string;
                runtimeSpecification?: string;
                deploymentRetention?: number;
                scopes?: ProjectKeyScopes[];
                description?: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                name: rest[0] as string,
                framework: rest[1] as Framework,
                buildRuntime: rest[2] as BuildRuntime,
                enabled: rest[3] as boolean,
                logging: rest[4] as boolean,
                timeout: rest[5] as number,
                installCommand: rest[6] as string,
                buildCommand: rest[7] as string,
                startCommand: rest[8] as string,
                outputDirectory: rest[9] as string,
                adapter: rest[10] as Adapter,
                installationId: rest[11] as string,
                fallbackFile: rest[12] as string,
                providerRepositoryId: rest[13] as string,
                providerBranch: rest[14] as string,
                providerSilentMode: rest[15] as boolean,
                providerRootDirectory: rest[16] as string,
                providerBranches: rest[17] as string[],
                providerPaths: rest[18] as string[],
                buildSpecification: rest[19] as string,
                runtimeSpecification: rest[20] as string,
                deploymentRetention: rest[21] as number,
                scopes: rest[22] as ProjectKeyScopes[],
                description: rest[23] as string,
            };
        }

        const siteId = params.siteId;
        const name = params.name;
        const framework = params.framework;
        const buildRuntime = params.buildRuntime;
        const enabled = params.enabled;
        const logging = params.logging;
        const timeout = params.timeout;
        const installCommand = params.installCommand;
        const buildCommand = params.buildCommand;
        const startCommand = params.startCommand;
        const outputDirectory = params.outputDirectory;
        const adapter = params.adapter;
        const installationId = params.installationId;
        const fallbackFile = params.fallbackFile;
        const providerRepositoryId = params.providerRepositoryId;
        const providerBranch = params.providerBranch;
        const providerSilentMode = params.providerSilentMode;
        const providerRootDirectory = params.providerRootDirectory;
        const providerBranches = params.providerBranches;
        const providerPaths = params.providerPaths;
        const buildSpecification = params.buildSpecification;
        const runtimeSpecification = params.runtimeSpecification;
        const deploymentRetention = params.deploymentRetention;
        const scopes = params.scopes;
        const description = params.description;

        if (typeof siteId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof framework === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "framework"',
            );
        }
        if (typeof buildRuntime === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "buildRuntime"',
            );
        }
        const apiPath = '/sites';
        const apiPayload: Payload = {};
        if (typeof siteId !== 'undefined') {
            apiPayload['siteId'] = siteId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof framework !== 'undefined') {
            apiPayload['framework'] = framework;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof logging !== 'undefined') {
            apiPayload['logging'] = logging;
        }
        if (typeof timeout !== 'undefined') {
            apiPayload['timeout'] = timeout;
        }
        if (typeof installCommand !== 'undefined') {
            apiPayload['installCommand'] = installCommand;
        }
        if (typeof buildCommand !== 'undefined') {
            apiPayload['buildCommand'] = buildCommand;
        }
        if (typeof startCommand !== 'undefined') {
            apiPayload['startCommand'] = startCommand;
        }
        if (typeof outputDirectory !== 'undefined') {
            apiPayload['outputDirectory'] = outputDirectory;
        }
        if (typeof buildRuntime !== 'undefined') {
            apiPayload['buildRuntime'] = buildRuntime;
        }
        if (typeof adapter !== 'undefined') {
            apiPayload['adapter'] = adapter;
        }
        if (typeof installationId !== 'undefined') {
            apiPayload['installationId'] = installationId;
        }
        if (typeof fallbackFile !== 'undefined') {
            apiPayload['fallbackFile'] = fallbackFile;
        }
        if (typeof providerRepositoryId !== 'undefined') {
            apiPayload['providerRepositoryId'] = providerRepositoryId;
        }
        if (typeof providerBranch !== 'undefined') {
            apiPayload['providerBranch'] = providerBranch;
        }
        if (typeof providerSilentMode !== 'undefined') {
            apiPayload['providerSilentMode'] = providerSilentMode;
        }
        if (typeof providerRootDirectory !== 'undefined') {
            apiPayload['providerRootDirectory'] = providerRootDirectory;
        }
        if (typeof providerBranches !== 'undefined') {
            apiPayload['providerBranches'] = providerBranches;
        }
        if (typeof providerPaths !== 'undefined') {
            apiPayload['providerPaths'] = providerPaths;
        }
        if (typeof buildSpecification !== 'undefined') {
            apiPayload['buildSpecification'] = buildSpecification;
        }
        if (typeof runtimeSpecification !== 'undefined') {
            apiPayload['runtimeSpecification'] = runtimeSpecification;
        }
        if (typeof deploymentRetention !== 'undefined') {
            apiPayload['deploymentRetention'] = deploymentRetention;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'site',
            models: {
                site: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        enabled: true,
                        live: true,
                        logging: true,
                        framework: '',
                        deploymentRetention: 0,
                        deploymentId: '',
                        deploymentCreatedAt: '',
                        deploymentScreenshotLight: '',
                        deploymentScreenshotDark: '',
                        latestDeploymentId: '',
                        latestDeploymentCreatedAt: '',
                        latestDeploymentStatus: '',
                        scopes: [],
                        vars: [],
                        timeout: 15,
                        installCommand: '',
                        buildCommand: '',
                        startCommand: '',
                        outputDirectory: '',
                        installationId: '',
                        providerRepositoryId: '',
                        providerBranch: '',
                        providerRootDirectory: '',
                        providerSilentMode: false,
                        providerBranches: [],
                        providerPaths: [],
                        buildSpecification: 's-1vcpu-512mb',
                        runtimeSpecification: 's-1vcpu-512mb',
                        buildRuntime: '',
                    },
                    properties: {
                        vars: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all frameworks that are currently available on the server instance.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.FrameworkList>}
     */
    listFrameworks(params?: { total?: boolean }): Promise<Models.FrameworkList>;
    /**
     * Get a list of all frameworks that are currently available on the server instance.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.FrameworkList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listFrameworks(total?: boolean): Promise<Models.FrameworkList>;
    listFrameworks(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.FrameworkList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/sites/frameworks';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'frameworkList',
            models: {
                frameworkList: {
                    defaults: {
                        total: 0,
                        frameworks: [],
                    },
                    properties: {
                        frameworks: {
                            items: 'framework',
                        },
                    },
                },
                framework: {
                    defaults: {
                        key: '',
                        name: '',
                        buildRuntime: '',
                        runtimes: [],
                        adapters: [],
                    },
                    properties: {
                        adapters: {
                            items: 'frameworkAdapter',
                        },
                    },
                },
                frameworkAdapter: {
                    defaults: {
                        key: '',
                        installCommand: '',
                        buildCommand: '',
                        outputDirectory: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List allowed site specifications for this instance.
     *
     * @param {string} params.type - Specification type to list. Can be one of: runtimes, builds. Defaults to runtimes.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SpecificationList>}
     */
    listSpecifications(params?: {
        type?: string;
        total?: boolean;
    }): Promise<Models.SpecificationList>;
    /**
     * List allowed site specifications for this instance.
     *
     * @param {string} type - Specification type to list. Can be one of: runtimes, builds. Defaults to runtimes.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SpecificationList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listSpecifications(
        type?: string,
        total?: boolean,
    ): Promise<Models.SpecificationList>;
    listSpecifications(
        paramsOrFirst?: { type?: string; total?: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.SpecificationList> {
        let params: { type?: string; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                type?: string;
                total?: boolean;
            };
        } else {
            params = {
                type: paramsOrFirst as string,
                total: rest[0] as boolean,
            };
        }

        const type = params.type;
        const total = params.total;

        const apiPath = '/sites/specifications';
        const apiPayload: Payload = {};
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'specificationList',
            models: {
                specificationList: {
                    defaults: {
                        total: 0,
                        specifications: [],
                    },
                    properties: {
                        specifications: {
                            items: 'specification',
                        },
                    },
                },
                specification: {
                    defaults: {
                        memory: 0,
                        cpus: 0,
                        enabled: false,
                        slug: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List available site templates. You can use template details in [createSite](/docs/references/cloud/server-nodejs/sites#create) method.
     *
     * @param {Framework[]} params.frameworks - List of frameworks allowed for filtering site templates. Maximum of 100 frameworks are allowed.
     * @param {SiteTemplateUseCase[]} params.useCases - List of use cases allowed for filtering site templates. Maximum of 100 use cases are allowed.
     * @param {number} params.limit - Limit the number of templates returned in the response. Default limit is 25, and maximum limit is 5000.
     * @param {number} params.offset - Offset the list of returned templates. Maximum offset is 5000.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TemplateSiteList>}
     */
    listTemplates(params?: {
        frameworks?: Framework[];
        useCases?: SiteTemplateUseCase[];
        limit?: number;
        offset?: number;
        total?: boolean;
    }): Promise<Models.TemplateSiteList>;
    /**
     * List available site templates. You can use template details in [createSite](/docs/references/cloud/server-nodejs/sites#create) method.
     *
     * @param {Framework[]} frameworks - List of frameworks allowed for filtering site templates. Maximum of 100 frameworks are allowed.
     * @param {SiteTemplateUseCase[]} useCases - List of use cases allowed for filtering site templates. Maximum of 100 use cases are allowed.
     * @param {number} limit - Limit the number of templates returned in the response. Default limit is 25, and maximum limit is 5000.
     * @param {number} offset - Offset the list of returned templates. Maximum offset is 5000.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TemplateSiteList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listTemplates(
        frameworks?: Framework[],
        useCases?: SiteTemplateUseCase[],
        limit?: number,
        offset?: number,
        total?: boolean,
    ): Promise<Models.TemplateSiteList>;
    listTemplates(
        paramsOrFirst?:
            | {
                  frameworks?: Framework[];
                  useCases?: SiteTemplateUseCase[];
                  limit?: number;
                  offset?: number;
                  total?: boolean;
              }
            | Framework[],
        ...rest: [SiteTemplateUseCase[]?, number?, number?, boolean?]
    ): Promise<Models.TemplateSiteList> {
        let params: {
            frameworks?: Framework[];
            useCases?: SiteTemplateUseCase[];
            limit?: number;
            offset?: number;
            total?: boolean;
        };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst) &&
                ('frameworks' in paramsOrFirst ||
                    'useCases' in paramsOrFirst ||
                    'limit' in paramsOrFirst ||
                    'offset' in paramsOrFirst ||
                    'total' in paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                frameworks?: Framework[];
                useCases?: SiteTemplateUseCase[];
                limit?: number;
                offset?: number;
                total?: boolean;
            };
        } else {
            params = {
                frameworks: paramsOrFirst as Framework[],
                useCases: rest[0] as SiteTemplateUseCase[],
                limit: rest[1] as number,
                offset: rest[2] as number,
                total: rest[3] as boolean,
            };
        }

        const frameworks = params.frameworks;
        const useCases = params.useCases;
        const limit = params.limit;
        const offset = params.offset;
        const total = params.total;

        const apiPath = '/sites/templates';
        const apiPayload: Payload = {};
        if (typeof frameworks !== 'undefined') {
            apiPayload['frameworks'] = frameworks;
        }
        if (typeof useCases !== 'undefined') {
            apiPayload['useCases'] = useCases;
        }
        if (typeof limit !== 'undefined') {
            apiPayload['limit'] = limit;
        }
        if (typeof offset !== 'undefined') {
            apiPayload['offset'] = offset;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'templateSiteList',
            models: {
                templateSiteList: {
                    defaults: {
                        total: 0,
                        templates: [],
                    },
                    properties: {
                        templates: {
                            items: 'templateSite',
                        },
                    },
                },
                templateSite: {
                    defaults: {
                        key: '',
                        name: '',
                        tagline: '',
                        screenshotDark: '',
                        screenshotLight: '',
                        useCases: [],
                        frameworks: [],
                        vcsProvider: '',
                        providerRepositoryId: '',
                        providerOwner: '',
                        providerVersion: '',
                        variables: [],
                    },
                    properties: {
                        frameworks: {
                            items: 'templateFramework',
                        },
                        variables: {
                            items: 'templateVariable',
                        },
                    },
                },
                templateFramework: {
                    defaults: {
                        key: '',
                        name: '',
                        installCommand: '',
                        buildCommand: '',
                        outputDirectory: '',
                        providerRootDirectory: '',
                        buildRuntime: '',
                        adapter: '',
                    },
                },
                templateVariable: {
                    defaults: {
                        name: '',
                        description: '',
                        value: '',
                        secret: false,
                        placeholder: '',
                        required: false,
                        type: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a site template using ID. You can use template details in [createSite](/docs/references/cloud/server-nodejs/sites#create) method.
     *
     * @param {string} params.templateId - Template ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TemplateSite>}
     */
    getTemplate(params: { templateId: string }): Promise<Models.TemplateSite>;
    /**
     * Get a site template using ID. You can use template details in [createSite](/docs/references/cloud/server-nodejs/sites#create) method.
     *
     * @param {string} templateId - Template ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TemplateSite>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getTemplate(templateId: string): Promise<Models.TemplateSite>;
    getTemplate(
        paramsOrFirst: { templateId: string } | string,
    ): Promise<Models.TemplateSite> {
        let params: { templateId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { templateId: string };
        } else {
            params = {
                templateId: paramsOrFirst as string,
            };
        }

        const templateId = params.templateId;

        if (typeof templateId === 'undefined' || templateId === '') {
            throw new AppwriteException(
                'Missing required parameter: "templateId"',
            );
        }
        const apiPath = '/sites/templates/{templateId}'.replace(
            '{templateId}',
            encodeURIComponent(String(templateId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'templateSite',
            models: {
                templateSite: {
                    defaults: {
                        key: '',
                        name: '',
                        tagline: '',
                        screenshotDark: '',
                        screenshotLight: '',
                        useCases: [],
                        frameworks: [],
                        vcsProvider: '',
                        providerRepositoryId: '',
                        providerOwner: '',
                        providerVersion: '',
                        variables: [],
                    },
                    properties: {
                        frameworks: {
                            items: 'templateFramework',
                        },
                        variables: {
                            items: 'templateVariable',
                        },
                    },
                },
                templateFramework: {
                    defaults: {
                        key: '',
                        name: '',
                        installCommand: '',
                        buildCommand: '',
                        outputDirectory: '',
                        providerRootDirectory: '',
                        buildRuntime: '',
                        adapter: '',
                    },
                },
                templateVariable: {
                    defaults: {
                        name: '',
                        description: '',
                        value: '',
                        secret: false,
                        placeholder: '',
                        required: false,
                        type: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a site by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     */
    get(params: { siteId: string }): Promise<Models.Site>;
    /**
     * Get a site by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    get(siteId: string): Promise<Models.Site>;
    get(paramsOrFirst: { siteId: string } | string): Promise<Models.Site> {
        let params: { siteId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { siteId: string };
        } else {
            params = {
                siteId: paramsOrFirst as string,
            };
        }

        const siteId = params.siteId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/sites/{siteId}'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'site',
            models: {
                site: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        enabled: true,
                        live: true,
                        logging: true,
                        framework: '',
                        deploymentRetention: 0,
                        deploymentId: '',
                        deploymentCreatedAt: '',
                        deploymentScreenshotLight: '',
                        deploymentScreenshotDark: '',
                        latestDeploymentId: '',
                        latestDeploymentCreatedAt: '',
                        latestDeploymentStatus: '',
                        scopes: [],
                        vars: [],
                        timeout: 15,
                        installCommand: '',
                        buildCommand: '',
                        startCommand: '',
                        outputDirectory: '',
                        installationId: '',
                        providerRepositoryId: '',
                        providerBranch: '',
                        providerRootDirectory: '',
                        providerSilentMode: false,
                        providerBranches: [],
                        providerPaths: [],
                        buildSpecification: 's-1vcpu-512mb',
                        runtimeSpecification: 's-1vcpu-512mb',
                        buildRuntime: '',
                    },
                    properties: {
                        vars: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update site by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.name - Site name. Max length: 128 chars.
     * @param {Framework} params.framework - Sites framework.
     * @param {boolean} params.enabled - Is site enabled? When set to 'disabled', users cannot access the site but Server SDKs with and API key can still access the site. No data is lost when this is toggled.
     * @param {boolean} params.logging - When disabled, request logs will exclude logs and errors, and site responses will be slightly faster.
     * @param {number} params.timeout - Maximum request time in seconds.
     * @param {string} params.installCommand - Install Command.
     * @param {string} params.buildCommand - Build Command.
     * @param {string} params.startCommand - Custom start command. Leave empty to use default.
     * @param {string} params.outputDirectory - Output Directory for site.
     * @param {BuildRuntime} params.buildRuntime - Runtime to use during build step.
     * @param {Adapter} params.adapter - Framework adapter defining rendering strategy. Allowed values are: static, ssr
     * @param {string} params.fallbackFile - Fallback file for single page application sites.
     * @param {string} params.installationId - Appwrite Installation ID for VCS (Version Control System) deployment.
     * @param {string | null} params.providerRepositoryId - Repository ID of the repo linked to the site.
     * @param {string} params.providerBranch - Production branch for the repo linked to the site.
     * @param {boolean} params.providerSilentMode - Is the VCS (Version Control System) connection in silent mode for the repo linked to the site? In silent mode, comments will not be made on commits and pull requests.
     * @param {string} params.providerRootDirectory - Path to site code in the linked repo.
     * @param {string[] | null} params.providerBranches - List of branch name patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all branches.
     * @param {string[] | null} params.providerPaths - List of file path patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all file changes.
     * @param {string | null} params.buildSpecification - Build specification for the site deployments.
     * @param {string} params.runtimeSpecification - Runtime specification for the SSR executions.
     * @param {number} params.deploymentRetention - Days to keep non-active deployments before deletion. Value 0 means all deployments will be kept.
     * @param {ProjectKeyScopes[] | null} params.scopes - List of scopes allowed for API key auto-generated for every site build and SSR execution. Maximum of 200 scopes are allowed.
     * @param {string | null} params.description - Optional description for the site.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     */
    update(params: {
        siteId: string;
        name: string;
        framework: Framework;
        enabled?: boolean;
        logging?: boolean;
        timeout?: number;
        installCommand?: string;
        buildCommand?: string;
        startCommand?: string;
        outputDirectory?: string;
        buildRuntime?: BuildRuntime;
        adapter?: Adapter;
        fallbackFile?: string;
        installationId?: string;
        providerRepositoryId?: string | null;
        providerBranch?: string;
        providerSilentMode?: boolean;
        providerRootDirectory?: string;
        providerBranches?: string[] | null;
        providerPaths?: string[] | null;
        buildSpecification?: string | null;
        runtimeSpecification?: string;
        deploymentRetention?: number;
        scopes?: ProjectKeyScopes[] | null;
        description?: string | null;
    }): Promise<Models.Site>;
    /**
     * Update site by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @param {string} name - Site name. Max length: 128 chars.
     * @param {Framework} framework - Sites framework.
     * @param {boolean} enabled - Is site enabled? When set to 'disabled', users cannot access the site but Server SDKs with and API key can still access the site. No data is lost when this is toggled.
     * @param {boolean} logging - When disabled, request logs will exclude logs and errors, and site responses will be slightly faster.
     * @param {number} timeout - Maximum request time in seconds.
     * @param {string} installCommand - Install Command.
     * @param {string} buildCommand - Build Command.
     * @param {string} startCommand - Custom start command. Leave empty to use default.
     * @param {string} outputDirectory - Output Directory for site.
     * @param {BuildRuntime} buildRuntime - Runtime to use during build step.
     * @param {Adapter} adapter - Framework adapter defining rendering strategy. Allowed values are: static, ssr
     * @param {string} fallbackFile - Fallback file for single page application sites.
     * @param {string} installationId - Appwrite Installation ID for VCS (Version Control System) deployment.
     * @param {string | null} providerRepositoryId - Repository ID of the repo linked to the site.
     * @param {string} providerBranch - Production branch for the repo linked to the site.
     * @param {boolean} providerSilentMode - Is the VCS (Version Control System) connection in silent mode for the repo linked to the site? In silent mode, comments will not be made on commits and pull requests.
     * @param {string} providerRootDirectory - Path to site code in the linked repo.
     * @param {string[] | null} providerBranches - List of branch name patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all branches.
     * @param {string[] | null} providerPaths - List of file path patterns to trigger automatic deployments. Supports wildcards. Leave empty to deploy on all file changes.
     * @param {string | null} buildSpecification - Build specification for the site deployments.
     * @param {string} runtimeSpecification - Runtime specification for the SSR executions.
     * @param {number} deploymentRetention - Days to keep non-active deployments before deletion. Value 0 means all deployments will be kept.
     * @param {ProjectKeyScopes[] | null} scopes - List of scopes allowed for API key auto-generated for every site build and SSR execution. Maximum of 200 scopes are allowed.
     * @param {string | null} description - Optional description for the site.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    update(
        siteId: string,
        name: string,
        framework: Framework,
        enabled?: boolean,
        logging?: boolean,
        timeout?: number,
        installCommand?: string,
        buildCommand?: string,
        startCommand?: string,
        outputDirectory?: string,
        buildRuntime?: BuildRuntime,
        adapter?: Adapter,
        fallbackFile?: string,
        installationId?: string,
        providerRepositoryId?: string | null,
        providerBranch?: string,
        providerSilentMode?: boolean,
        providerRootDirectory?: string,
        providerBranches?: string[] | null,
        providerPaths?: string[] | null,
        buildSpecification?: string | null,
        runtimeSpecification?: string,
        deploymentRetention?: number,
        scopes?: ProjectKeyScopes[] | null,
        description?: string | null,
    ): Promise<Models.Site>;
    update(
        paramsOrFirst:
            | {
                  siteId: string;
                  name: string;
                  framework: Framework;
                  enabled?: boolean;
                  logging?: boolean;
                  timeout?: number;
                  installCommand?: string;
                  buildCommand?: string;
                  startCommand?: string;
                  outputDirectory?: string;
                  buildRuntime?: BuildRuntime;
                  adapter?: Adapter;
                  fallbackFile?: string;
                  installationId?: string;
                  providerRepositoryId?: string | null;
                  providerBranch?: string;
                  providerSilentMode?: boolean;
                  providerRootDirectory?: string;
                  providerBranches?: string[] | null;
                  providerPaths?: string[] | null;
                  buildSpecification?: string | null;
                  runtimeSpecification?: string;
                  deploymentRetention?: number;
                  scopes?: ProjectKeyScopes[] | null;
                  description?: string | null;
              }
            | string,
        ...rest: [
            string?,
            Framework?,
            boolean?,
            boolean?,
            number?,
            string?,
            string?,
            string?,
            string?,
            BuildRuntime?,
            Adapter?,
            string?,
            string?,
            (string | null)?,
            string?,
            boolean?,
            string?,
            (string[] | null)?,
            (string[] | null)?,
            (string | null)?,
            string?,
            number?,
            (ProjectKeyScopes[] | null)?,
            (string | null)?,
        ]
    ): Promise<Models.Site> {
        let params: {
            siteId: string;
            name: string;
            framework: Framework;
            enabled?: boolean;
            logging?: boolean;
            timeout?: number;
            installCommand?: string;
            buildCommand?: string;
            startCommand?: string;
            outputDirectory?: string;
            buildRuntime?: BuildRuntime;
            adapter?: Adapter;
            fallbackFile?: string;
            installationId?: string;
            providerRepositoryId?: string | null;
            providerBranch?: string;
            providerSilentMode?: boolean;
            providerRootDirectory?: string;
            providerBranches?: string[] | null;
            providerPaths?: string[] | null;
            buildSpecification?: string | null;
            runtimeSpecification?: string;
            deploymentRetention?: number;
            scopes?: ProjectKeyScopes[] | null;
            description?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                name: string;
                framework: Framework;
                enabled?: boolean;
                logging?: boolean;
                timeout?: number;
                installCommand?: string;
                buildCommand?: string;
                startCommand?: string;
                outputDirectory?: string;
                buildRuntime?: BuildRuntime;
                adapter?: Adapter;
                fallbackFile?: string;
                installationId?: string;
                providerRepositoryId?: string | null;
                providerBranch?: string;
                providerSilentMode?: boolean;
                providerRootDirectory?: string;
                providerBranches?: string[] | null;
                providerPaths?: string[] | null;
                buildSpecification?: string | null;
                runtimeSpecification?: string;
                deploymentRetention?: number;
                scopes?: ProjectKeyScopes[] | null;
                description?: string | null;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                name: rest[0] as string,
                framework: rest[1] as Framework,
                enabled: rest[2] as boolean,
                logging: rest[3] as boolean,
                timeout: rest[4] as number,
                installCommand: rest[5] as string,
                buildCommand: rest[6] as string,
                startCommand: rest[7] as string,
                outputDirectory: rest[8] as string,
                buildRuntime: rest[9] as BuildRuntime,
                adapter: rest[10] as Adapter,
                fallbackFile: rest[11] as string,
                installationId: rest[12] as string,
                providerRepositoryId: rest[13] as string | null,
                providerBranch: rest[14] as string,
                providerSilentMode: rest[15] as boolean,
                providerRootDirectory: rest[16] as string,
                providerBranches: rest[17] as string[] | null,
                providerPaths: rest[18] as string[] | null,
                buildSpecification: rest[19] as string | null,
                runtimeSpecification: rest[20] as string,
                deploymentRetention: rest[21] as number,
                scopes: rest[22] as ProjectKeyScopes[] | null,
                description: rest[23] as string | null,
            };
        }

        const siteId = params.siteId;
        const name = params.name;
        const framework = params.framework;
        const enabled = params.enabled;
        const logging = params.logging;
        const timeout = params.timeout;
        const installCommand = params.installCommand;
        const buildCommand = params.buildCommand;
        const startCommand = params.startCommand;
        const outputDirectory = params.outputDirectory;
        const buildRuntime = params.buildRuntime;
        const adapter = params.adapter;
        const fallbackFile = params.fallbackFile;
        const installationId = params.installationId;
        const providerRepositoryId = params.providerRepositoryId;
        const providerBranch = params.providerBranch;
        const providerSilentMode = params.providerSilentMode;
        const providerRootDirectory = params.providerRootDirectory;
        const providerBranches = params.providerBranches;
        const providerPaths = params.providerPaths;
        const buildSpecification = params.buildSpecification;
        const runtimeSpecification = params.runtimeSpecification;
        const deploymentRetention = params.deploymentRetention;
        const scopes = params.scopes;
        const description = params.description;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof framework === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "framework"',
            );
        }
        const apiPath = '/sites/{siteId}'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof framework !== 'undefined') {
            apiPayload['framework'] = framework;
        }
        if (typeof enabled !== 'undefined') {
            apiPayload['enabled'] = enabled;
        }
        if (typeof logging !== 'undefined') {
            apiPayload['logging'] = logging;
        }
        if (typeof timeout !== 'undefined') {
            apiPayload['timeout'] = timeout;
        }
        if (typeof installCommand !== 'undefined') {
            apiPayload['installCommand'] = installCommand;
        }
        if (typeof buildCommand !== 'undefined') {
            apiPayload['buildCommand'] = buildCommand;
        }
        if (typeof startCommand !== 'undefined') {
            apiPayload['startCommand'] = startCommand;
        }
        if (typeof outputDirectory !== 'undefined') {
            apiPayload['outputDirectory'] = outputDirectory;
        }
        if (typeof buildRuntime !== 'undefined') {
            apiPayload['buildRuntime'] = buildRuntime;
        }
        if (typeof adapter !== 'undefined') {
            apiPayload['adapter'] = adapter;
        }
        if (typeof fallbackFile !== 'undefined') {
            apiPayload['fallbackFile'] = fallbackFile;
        }
        if (typeof installationId !== 'undefined') {
            apiPayload['installationId'] = installationId;
        }
        if (typeof providerRepositoryId !== 'undefined') {
            apiPayload['providerRepositoryId'] = providerRepositoryId;
        }
        if (typeof providerBranch !== 'undefined') {
            apiPayload['providerBranch'] = providerBranch;
        }
        if (typeof providerSilentMode !== 'undefined') {
            apiPayload['providerSilentMode'] = providerSilentMode;
        }
        if (typeof providerRootDirectory !== 'undefined') {
            apiPayload['providerRootDirectory'] = providerRootDirectory;
        }
        if (typeof providerBranches !== 'undefined') {
            apiPayload['providerBranches'] = providerBranches;
        }
        if (typeof providerPaths !== 'undefined') {
            apiPayload['providerPaths'] = providerPaths;
        }
        if (typeof buildSpecification !== 'undefined') {
            apiPayload['buildSpecification'] = buildSpecification;
        }
        if (typeof runtimeSpecification !== 'undefined') {
            apiPayload['runtimeSpecification'] = runtimeSpecification;
        }
        if (typeof deploymentRetention !== 'undefined') {
            apiPayload['deploymentRetention'] = deploymentRetention;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'site',
            models: {
                site: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        enabled: true,
                        live: true,
                        logging: true,
                        framework: '',
                        deploymentRetention: 0,
                        deploymentId: '',
                        deploymentCreatedAt: '',
                        deploymentScreenshotLight: '',
                        deploymentScreenshotDark: '',
                        latestDeploymentId: '',
                        latestDeploymentCreatedAt: '',
                        latestDeploymentStatus: '',
                        scopes: [],
                        vars: [],
                        timeout: 15,
                        installCommand: '',
                        buildCommand: '',
                        startCommand: '',
                        outputDirectory: '',
                        installationId: '',
                        providerRepositoryId: '',
                        providerBranch: '',
                        providerRootDirectory: '',
                        providerSilentMode: false,
                        providerBranches: [],
                        providerPaths: [],
                        buildSpecification: 's-1vcpu-512mb',
                        runtimeSpecification: 's-1vcpu-512mb',
                        buildRuntime: '',
                    },
                    properties: {
                        vars: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a site by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(params: { siteId: string }): Promise<{}>;
    /**
     * Delete a site by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    delete(siteId: string): Promise<{}>;
    delete(paramsOrFirst: { siteId: string } | string): Promise<{}> {
        let params: { siteId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { siteId: string };
        } else {
            params = {
                siteId: paramsOrFirst as string,
            };
        }

        const siteId = params.siteId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/sites/{siteId}'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the site active deployment. Use this endpoint to switch the code deployment that should be used when visitor opens your site.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     */
    updateSiteDeployment(params: {
        siteId: string;
        deploymentId: string;
    }): Promise<Models.Site>;
    /**
     * Update the site active deployment. Use this endpoint to switch the code deployment that should be used when visitor opens your site.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Site>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSiteDeployment(
        siteId: string,
        deploymentId: string,
    ): Promise<Models.Site>;
    updateSiteDeployment(
        paramsOrFirst: { siteId: string; deploymentId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Site> {
        let params: { siteId: string; deploymentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployment'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof deploymentId !== 'undefined') {
            apiPayload['deploymentId'] = deploymentId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'site',
            models: {
                site: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        enabled: true,
                        live: true,
                        logging: true,
                        framework: '',
                        deploymentRetention: 0,
                        deploymentId: '',
                        deploymentCreatedAt: '',
                        deploymentScreenshotLight: '',
                        deploymentScreenshotDark: '',
                        latestDeploymentId: '',
                        latestDeploymentCreatedAt: '',
                        latestDeploymentStatus: '',
                        scopes: [],
                        vars: [],
                        timeout: 15,
                        installCommand: '',
                        buildCommand: '',
                        startCommand: '',
                        outputDirectory: '',
                        installationId: '',
                        providerRepositoryId: '',
                        providerBranch: '',
                        providerRootDirectory: '',
                        providerSilentMode: false,
                        providerBranches: [],
                        providerPaths: [],
                        buildSpecification: 's-1vcpu-512mb',
                        runtimeSpecification: 's-1vcpu-512mb',
                        buildRuntime: '',
                    },
                    properties: {
                        vars: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all the site's code deployments. You can use the query params to filter your results.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: buildSize, sourceSize, totalSize, buildDuration, status, activate, type
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DeploymentList>}
     */
    listDeployments(params: {
        siteId: string;
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.DeploymentList>;
    /**
     * Get a list of all the site's code deployments. You can use the query params to filter your results.
     *
     * @param {string} siteId - Site ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: buildSize, sourceSize, totalSize, buildDuration, status, activate, type
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.DeploymentList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listDeployments(
        siteId: string,
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.DeploymentList>;
    listDeployments(
        paramsOrFirst:
            | {
                  siteId: string;
                  queries?: string[];
                  search?: string;
                  total?: boolean;
              }
            | string,
        ...rest: [string[]?, string?, boolean?]
    ): Promise<Models.DeploymentList> {
        let params: {
            siteId: string;
            queries?: string[];
            search?: string;
            total?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                queries: rest[0] as string[],
                search: rest[1] as string,
                total: rest[2] as boolean,
            };
        }

        const siteId = params.siteId;
        const queries = params.queries;
        const search = params.search;
        const total = params.total;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/sites/{siteId}/deployments'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deploymentList',
            models: {
                deploymentList: {
                    defaults: {
                        total: 0,
                        deployments: [],
                    },
                    properties: {
                        deployments: {
                            items: 'deployment',
                        },
                    },
                },
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new site code deployment. Use this endpoint to upload a new version of your site code. To activate your newly uploaded code, you'll need to update the site's deployment to use your new deployment ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {File} params.code - Gzip file with your code package. When used with the Appwrite CLI, pass the path to your code directory, and the CLI will automatically package your code. Use a path that is within the current directory.
     * @param {string} params.installCommand - Install Commands.
     * @param {string} params.buildCommand - Build Commands.
     * @param {string} params.outputDirectory - Output Directory.
     * @param {boolean} params.activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    createDeployment(params: {
        siteId: string;
        code: File;
        installCommand?: string;
        buildCommand?: string;
        outputDirectory?: string;
        activate?: boolean;
        onProgress?: (progress: UploadProgress) => void;
    }): Promise<Models.Deployment>;
    /**
     * Create a new site code deployment. Use this endpoint to upload a new version of your site code. To activate your newly uploaded code, you'll need to update the site's deployment to use your new deployment ID.
     *
     * @param {string} siteId - Site ID.
     * @param {File} code - Gzip file with your code package. When used with the Appwrite CLI, pass the path to your code directory, and the CLI will automatically package your code. Use a path that is within the current directory.
     * @param {string} installCommand - Install Commands.
     * @param {string} buildCommand - Build Commands.
     * @param {string} outputDirectory - Output Directory.
     * @param {boolean} activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createDeployment(
        siteId: string,
        code: File,
        installCommand?: string,
        buildCommand?: string,
        outputDirectory?: string,
        activate?: boolean,
        onProgress?: (progress: UploadProgress) => void,
    ): Promise<Models.Deployment>;
    createDeployment(
        paramsOrFirst:
            | {
                  siteId: string;
                  code: File;
                  installCommand?: string;
                  buildCommand?: string;
                  outputDirectory?: string;
                  activate?: boolean;
                  onProgress?: (progress: UploadProgress) => void;
              }
            | string,
        ...rest: [
            File?,
            string?,
            string?,
            string?,
            boolean?,
            ((progress: UploadProgress) => void)?,
        ]
    ): Promise<Models.Deployment> {
        let params: {
            siteId: string;
            code: File;
            installCommand?: string;
            buildCommand?: string;
            outputDirectory?: string;
            activate?: boolean;
        };
        let onProgress: (progress: UploadProgress) => void;

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                code: File;
                installCommand?: string;
                buildCommand?: string;
                outputDirectory?: string;
                activate?: boolean;
            };
            onProgress = paramsOrFirst?.onProgress as (
                progress: UploadProgress,
            ) => void;
        } else {
            params = {
                siteId: paramsOrFirst as string,
                code: rest[0] as File,
                installCommand: rest[1] as string,
                buildCommand: rest[2] as string,
                outputDirectory: rest[3] as string,
                activate: rest[4] as boolean,
            };
            onProgress = rest[5] as (progress: UploadProgress) => void;
        }

        const siteId = params.siteId;
        const code = params.code;
        const installCommand = params.installCommand;
        const buildCommand = params.buildCommand;
        const outputDirectory = params.outputDirectory;
        const activate = params.activate;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof code === 'undefined') {
            throw new AppwriteException('Missing required parameter: "code"');
        }
        const apiPath = '/sites/{siteId}/deployments'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof installCommand !== 'undefined') {
            apiPayload['installCommand'] = installCommand;
        }
        if (typeof buildCommand !== 'undefined') {
            apiPayload['buildCommand'] = buildCommand;
        }
        if (typeof outputDirectory !== 'undefined') {
            apiPayload['outputDirectory'] = outputDirectory;
        }
        if (typeof code !== 'undefined') {
            apiPayload['code'] = code;
        }
        if (typeof activate !== 'undefined') {
            apiPayload['activate'] = activate;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'multipart/form-data',
            accept: 'application/json',
        };

        return this.client.chunkedUpload(
            'post',
            uri,
            apiHeaders,
            apiPayload,
            onProgress,
        );
    }

    /**
     * Create a new build for an existing site deployment. This endpoint allows you to rebuild a deployment with the updated site configuration, including its commands and output directory if they have been modified. The build process will be queued and executed asynchronously. The original deployment's code will be preserved and used for the new build.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    createDuplicateDeployment(params: {
        siteId: string;
        deploymentId: string;
    }): Promise<Models.Deployment>;
    /**
     * Create a new build for an existing site deployment. This endpoint allows you to rebuild a deployment with the updated site configuration, including its commands and output directory if they have been modified. The build process will be queued and executed asynchronously. The original deployment's code will be preserved and used for the new build.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createDuplicateDeployment(
        siteId: string,
        deploymentId: string,
    ): Promise<Models.Deployment>;
    createDuplicateDeployment(
        paramsOrFirst: { siteId: string; deploymentId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Deployment> {
        let params: { siteId: string; deploymentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/duplicate'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof deploymentId !== 'undefined') {
            apiPayload['deploymentId'] = deploymentId;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deployment',
            models: {
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a deployment based on a template.
     *
     * Use this endpoint with combination of [listTemplates](https://appwrite.io/docs/products/sites/templates) to find the template details.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.repository - Repository name of the template.
     * @param {string} params.owner - The name of the owner of the template.
     * @param {string} params.rootDirectory - Path to site code in the template repo.
     * @param {TemplateReferenceType} params.type - Type for the reference provided. Can be commit, branch, or tag
     * @param {string} params.reference - Reference value, can be a commit hash, branch name, or release tag
     * @param {boolean} params.activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    createTemplateDeployment(params: {
        siteId: string;
        repository: string;
        owner: string;
        rootDirectory: string;
        type: TemplateReferenceType;
        reference: string;
        activate?: boolean;
    }): Promise<Models.Deployment>;
    /**
     * Create a deployment based on a template.
     *
     * Use this endpoint with combination of [listTemplates](https://appwrite.io/docs/products/sites/templates) to find the template details.
     *
     * @param {string} siteId - Site ID.
     * @param {string} repository - Repository name of the template.
     * @param {string} owner - The name of the owner of the template.
     * @param {string} rootDirectory - Path to site code in the template repo.
     * @param {TemplateReferenceType} type - Type for the reference provided. Can be commit, branch, or tag
     * @param {string} reference - Reference value, can be a commit hash, branch name, or release tag
     * @param {boolean} activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createTemplateDeployment(
        siteId: string,
        repository: string,
        owner: string,
        rootDirectory: string,
        type: TemplateReferenceType,
        reference: string,
        activate?: boolean,
    ): Promise<Models.Deployment>;
    createTemplateDeployment(
        paramsOrFirst:
            | {
                  siteId: string;
                  repository: string;
                  owner: string;
                  rootDirectory: string;
                  type: TemplateReferenceType;
                  reference: string;
                  activate?: boolean;
              }
            | string,
        ...rest: [
            string?,
            string?,
            string?,
            TemplateReferenceType?,
            string?,
            boolean?,
        ]
    ): Promise<Models.Deployment> {
        let params: {
            siteId: string;
            repository: string;
            owner: string;
            rootDirectory: string;
            type: TemplateReferenceType;
            reference: string;
            activate?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                repository: string;
                owner: string;
                rootDirectory: string;
                type: TemplateReferenceType;
                reference: string;
                activate?: boolean;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                repository: rest[0] as string,
                owner: rest[1] as string,
                rootDirectory: rest[2] as string,
                type: rest[3] as TemplateReferenceType,
                reference: rest[4] as string,
                activate: rest[5] as boolean,
            };
        }

        const siteId = params.siteId;
        const repository = params.repository;
        const owner = params.owner;
        const rootDirectory = params.rootDirectory;
        const type = params.type;
        const reference = params.reference;
        const activate = params.activate;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof repository === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "repository"',
            );
        }
        if (typeof owner === 'undefined') {
            throw new AppwriteException('Missing required parameter: "owner"');
        }
        if (typeof rootDirectory === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "rootDirectory"',
            );
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        if (typeof reference === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "reference"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/template'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof repository !== 'undefined') {
            apiPayload['repository'] = repository;
        }
        if (typeof owner !== 'undefined') {
            apiPayload['owner'] = owner;
        }
        if (typeof rootDirectory !== 'undefined') {
            apiPayload['rootDirectory'] = rootDirectory;
        }
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        if (typeof reference !== 'undefined') {
            apiPayload['reference'] = reference;
        }
        if (typeof activate !== 'undefined') {
            apiPayload['activate'] = activate;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deployment',
            models: {
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a deployment when a site is connected to VCS.
     *
     * This endpoint lets you create deployment from a branch, commit, or a tag.
     *
     * @param {string} params.siteId - Site ID.
     * @param {VCSReferenceType} params.type - Type of reference passed. Allowed values are: branch, commit
     * @param {string} params.reference - VCS reference to create deployment from. Depending on type this can be: branch name, commit hash
     * @param {boolean} params.activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    createVcsDeployment(params: {
        siteId: string;
        type: VCSReferenceType;
        reference: string;
        activate?: boolean;
    }): Promise<Models.Deployment>;
    /**
     * Create a deployment when a site is connected to VCS.
     *
     * This endpoint lets you create deployment from a branch, commit, or a tag.
     *
     * @param {string} siteId - Site ID.
     * @param {VCSReferenceType} type - Type of reference passed. Allowed values are: branch, commit
     * @param {string} reference - VCS reference to create deployment from. Depending on type this can be: branch name, commit hash
     * @param {boolean} activate - Automatically activate the deployment when it is finished building.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createVcsDeployment(
        siteId: string,
        type: VCSReferenceType,
        reference: string,
        activate?: boolean,
    ): Promise<Models.Deployment>;
    createVcsDeployment(
        paramsOrFirst:
            | {
                  siteId: string;
                  type: VCSReferenceType;
                  reference: string;
                  activate?: boolean;
              }
            | string,
        ...rest: [VCSReferenceType?, string?, boolean?]
    ): Promise<Models.Deployment> {
        let params: {
            siteId: string;
            type: VCSReferenceType;
            reference: string;
            activate?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                type: VCSReferenceType;
                reference: string;
                activate?: boolean;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                type: rest[0] as VCSReferenceType,
                reference: rest[1] as string,
                activate: rest[2] as boolean,
            };
        }

        const siteId = params.siteId;
        const type = params.type;
        const reference = params.reference;
        const activate = params.activate;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        if (typeof reference === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "reference"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/vcs'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        if (typeof reference !== 'undefined') {
            apiPayload['reference'] = reference;
        }
        if (typeof activate !== 'undefined') {
            apiPayload['activate'] = activate;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deployment',
            models: {
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a site deployment by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    getDeployment(params: {
        siteId: string;
        deploymentId: string;
    }): Promise<Models.Deployment>;
    /**
     * Get a site deployment by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getDeployment(
        siteId: string,
        deploymentId: string,
    ): Promise<Models.Deployment>;
    getDeployment(
        paramsOrFirst: { siteId: string; deploymentId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Deployment> {
        let params: { siteId: string; deploymentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined' || deploymentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/{deploymentId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace(
                '{deploymentId}',
                encodeURIComponent(String(deploymentId)),
            );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deployment',
            models: {
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a site deployment by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteDeployment(params: {
        siteId: string;
        deploymentId: string;
    }): Promise<{}>;
    /**
     * Delete a site deployment by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteDeployment(siteId: string, deploymentId: string): Promise<{}>;
    deleteDeployment(
        paramsOrFirst: { siteId: string; deploymentId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { siteId: string; deploymentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined' || deploymentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/{deploymentId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace(
                '{deploymentId}',
                encodeURIComponent(String(deploymentId)),
            );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a site deployment content by its unique ID. The endpoint response return with a 'Content-Disposition: attachment' header that tells the browser to start downloading the file to user downloads directory.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @param {DeploymentDownloadType} params.type - Deployment file to download. Can be: "source", "output".
     * @param {string} params.token - Presigned source-download token for accessing this deployment without a session (jobs-service).
     * @throws {AppwriteException}
     * @returns {string}
     */
    getDeploymentDownload(params: {
        siteId: string;
        deploymentId: string;
        type?: DeploymentDownloadType;
        token?: string;
    }): string;
    /**
     * Get a site deployment content by its unique ID. The endpoint response return with a 'Content-Disposition: attachment' header that tells the browser to start downloading the file to user downloads directory.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @param {DeploymentDownloadType} type - Deployment file to download. Can be: "source", "output".
     * @param {string} token - Presigned source-download token for accessing this deployment without a session (jobs-service).
     * @throws {AppwriteException}
     * @returns {string}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getDeploymentDownload(
        siteId: string,
        deploymentId: string,
        type?: DeploymentDownloadType,
        token?: string,
    ): string;
    getDeploymentDownload(
        paramsOrFirst:
            | {
                  siteId: string;
                  deploymentId: string;
                  type?: DeploymentDownloadType;
                  token?: string;
              }
            | string,
        ...rest: [string?, DeploymentDownloadType?, string?]
    ): string {
        let params: {
            siteId: string;
            deploymentId: string;
            type?: DeploymentDownloadType;
            token?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
                type?: DeploymentDownloadType;
                token?: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
                type: rest[1] as DeploymentDownloadType,
                token: rest[2] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;
        const type = params.type;
        const token = params.token;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined' || deploymentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/{deploymentId}/download'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace(
                '{deploymentId}',
                encodeURIComponent(String(deploymentId)),
            );
        const apiPayload: Payload = {};
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        if (typeof token !== 'undefined') {
            apiPayload['token'] = token;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        apiPayload['project'] = this.client.config.project;
        apiPayload['impersonateuserid'] = this.client.config.impersonateuserid;

        for (const [key, value] of Object.entries(Client.flatten(apiPayload))) {
            uri.searchParams.append(key, value);
        }

        return uri.toString();
    }

    /**
     * Cancel an ongoing site deployment build. If the build is already in progress, it will be stopped and marked as canceled. If the build hasn't started yet, it will be marked as canceled without executing. You cannot cancel builds that have already completed (status 'ready') or failed. The response includes the final build status and details.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     */
    updateDeploymentStatus(params: {
        siteId: string;
        deploymentId: string;
    }): Promise<Models.Deployment>;
    /**
     * Cancel an ongoing site deployment build. If the build is already in progress, it will be stopped and marked as canceled. If the build hasn't started yet, it will be marked as canceled without executing. You cannot cancel builds that have already completed (status 'ready') or failed. The response includes the final build status and details.
     *
     * @param {string} siteId - Site ID.
     * @param {string} deploymentId - Deployment ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Deployment>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateDeploymentStatus(
        siteId: string,
        deploymentId: string,
    ): Promise<Models.Deployment>;
    updateDeploymentStatus(
        paramsOrFirst: { siteId: string; deploymentId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Deployment> {
        let params: { siteId: string; deploymentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                deploymentId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                deploymentId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const deploymentId = params.deploymentId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof deploymentId === 'undefined' || deploymentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "deploymentId"',
            );
        }
        const apiPath = '/sites/{siteId}/deployments/{deploymentId}/status'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace(
                '{deploymentId}',
                encodeURIComponent(String(deploymentId)),
            );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'deployment',
            models: {
                deployment: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        type: '',
                        resourceId: '',
                        resourceType: '',
                        entrypoint: '',
                        sourceSize: 0,
                        buildSize: 0,
                        totalSize: 0,
                        buildId: '',
                        activate: false,
                        screenshotLight: '',
                        screenshotDark: '',
                        buildLogs: '',
                        buildDuration: 0,
                        providerRepositoryName: '',
                        providerRepositoryOwner: '',
                        providerRepositoryUrl: '',
                        providerCommitHash: '',
                        providerCommitAuthorUrl: '',
                        providerCommitAuthor: '',
                        providerCommitMessage: '',
                        providerCommitUrl: '',
                        providerBranch: '',
                        providerBranchUrl: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all site logs. You can use the query params to filter your results.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: trigger, status, responseStatusCode, duration, requestMethod, requestPath, deploymentId
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ExecutionList>}
     */
    listLogs(params: {
        siteId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ExecutionList>;
    /**
     * Get a list of all site logs. You can use the query params to filter your results.
     *
     * @param {string} siteId - Site ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: trigger, status, responseStatusCode, duration, requestMethod, requestPath, deploymentId
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ExecutionList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listLogs(
        siteId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ExecutionList>;
    listLogs(
        paramsOrFirst:
            { siteId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.ExecutionList> {
        let params: { siteId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const siteId = params.siteId;
        const queries = params.queries;
        const total = params.total;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/sites/{siteId}/logs'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'executionList',
            models: {
                executionList: {
                    defaults: {
                        total: 0,
                        executions: [],
                    },
                    properties: {
                        executions: {
                            items: 'execution',
                        },
                    },
                },
                execution: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        resourceId: '',
                        resourceType: 'functions',
                        deploymentId: '',
                        requestMethod: '',
                        requestPath: '',
                        requestHeaders: [],
                        responseStatusCode: 0,
                        responseBody: '',
                        responseHeaders: [],
                        logs: '',
                        errors: '',
                        duration: 0,
                    },
                    properties: {
                        requestHeaders: {
                            items: 'headers',
                        },
                        responseHeaders: {
                            items: 'headers',
                        },
                    },
                },
                headers: {
                    defaults: {
                        name: '',
                        value: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a site request log by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.logId - Log ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     */
    getLog(params: {
        siteId: string;
        logId: string;
    }): Promise<Models.Execution>;
    /**
     * Get a site request log by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @param {string} logId - Log ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getLog(siteId: string, logId: string): Promise<Models.Execution>;
    getLog(
        paramsOrFirst: { siteId: string; logId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Execution> {
        let params: { siteId: string; logId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { siteId: string; logId: string };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                logId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const logId = params.logId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof logId === 'undefined' || logId === '') {
            throw new AppwriteException('Missing required parameter: "logId"');
        }
        const apiPath = '/sites/{siteId}/logs/{logId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace('{logId}', encodeURIComponent(String(logId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'execution',
            models: {
                execution: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        resourceId: '',
                        resourceType: 'functions',
                        deploymentId: '',
                        requestMethod: '',
                        requestPath: '',
                        requestHeaders: [],
                        responseStatusCode: 0,
                        responseBody: '',
                        responseHeaders: [],
                        logs: '',
                        errors: '',
                        duration: 0,
                    },
                    properties: {
                        requestHeaders: {
                            items: 'headers',
                        },
                        responseHeaders: {
                            items: 'headers',
                        },
                    },
                },
                headers: {
                    defaults: {
                        name: '',
                        value: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a site log by its unique ID.
     *
     * @param {string} params.siteId - Site ID.
     * @param {string} params.logId - Log ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteLog(params: { siteId: string; logId: string }): Promise<{}>;
    /**
     * Delete a site log by its unique ID.
     *
     * @param {string} siteId - Site ID.
     * @param {string} logId - Log ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteLog(siteId: string, logId: string): Promise<{}>;
    deleteLog(
        paramsOrFirst: { siteId: string; logId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { siteId: string; logId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { siteId: string; logId: string };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                logId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const logId = params.logId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof logId === 'undefined' || logId === '') {
            throw new AppwriteException('Missing required parameter: "logId"');
        }
        const apiPath = '/sites/{siteId}/logs/{logId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace('{logId}', encodeURIComponent(String(logId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of all variables of a specific site.
     *
     * @param {string} params.siteId - Site unique ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: key, resourceType, resourceId, secret
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.VariableList>}
     */
    listVariables(params: {
        siteId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.VariableList>;
    /**
     * Get a list of all variables of a specific site.
     *
     * @param {string} siteId - Site unique ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: key, resourceType, resourceId, secret
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.VariableList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listVariables(
        siteId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.VariableList>;
    listVariables(
        paramsOrFirst:
            { siteId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.VariableList> {
        let params: { siteId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const siteId = params.siteId;
        const queries = params.queries;
        const total = params.total;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/sites/{siteId}/variables'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variableList',
            models: {
                variableList: {
                    defaults: {
                        total: 0,
                        variables: [],
                    },
                    properties: {
                        variables: {
                            items: 'variable',
                        },
                    },
                },
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new site variable. These variables can be accessed during build and runtime (server-side rendering) as environment variables.
     *
     * @param {string} params.siteId - Site unique ID.
     * @param {string} params.variableId - Variable ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string} params.value - Variable value. Max length: 8192 chars.
     * @param {boolean} params.secret - Secret variables can be updated or deleted, but only sites can read them during build and runtime.
     * @param {string} params.description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    createVariable(params: {
        siteId: string;
        variableId: string;
        key: string;
        value: string;
        secret?: boolean;
        description?: string;
    }): Promise<Models.Variable>;
    /**
     * Create a new site variable. These variables can be accessed during build and runtime (server-side rendering) as environment variables.
     *
     * @param {string} siteId - Site unique ID.
     * @param {string} variableId - Variable ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string} value - Variable value. Max length: 8192 chars.
     * @param {boolean} secret - Secret variables can be updated or deleted, but only sites can read them during build and runtime.
     * @param {string} description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createVariable(
        siteId: string,
        variableId: string,
        key: string,
        value: string,
        secret?: boolean,
        description?: string,
    ): Promise<Models.Variable>;
    createVariable(
        paramsOrFirst:
            | {
                  siteId: string;
                  variableId: string;
                  key: string;
                  value: string;
                  secret?: boolean;
                  description?: string;
              }
            | string,
        ...rest: [string?, string?, string?, boolean?, string?]
    ): Promise<Models.Variable> {
        let params: {
            siteId: string;
            variableId: string;
            key: string;
            value: string;
            secret?: boolean;
            description?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                variableId: string;
                key: string;
                value: string;
                secret?: boolean;
                description?: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                variableId: rest[0] as string,
                key: rest[1] as string,
                value: rest[2] as string,
                secret: rest[3] as boolean,
                description: rest[4] as string,
            };
        }

        const siteId = params.siteId;
        const variableId = params.variableId;
        const key = params.key;
        const value = params.value;
        const secret = params.secret;
        const description = params.description;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof variableId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        if (typeof key === 'undefined') {
            throw new AppwriteException('Missing required parameter: "key"');
        }
        if (typeof value === 'undefined') {
            throw new AppwriteException('Missing required parameter: "value"');
        }
        const apiPath = '/sites/{siteId}/variables'.replace(
            '{siteId}',
            encodeURIComponent(String(siteId)),
        );
        const apiPayload: Payload = {};
        if (typeof variableId !== 'undefined') {
            apiPayload['variableId'] = variableId;
        }
        if (typeof key !== 'undefined') {
            apiPayload['key'] = key;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a variable by its unique ID.
     *
     * @param {string} params.siteId - Site unique ID.
     * @param {string} params.variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    getVariable(params: {
        siteId: string;
        variableId: string;
    }): Promise<Models.Variable>;
    /**
     * Get a variable by its unique ID.
     *
     * @param {string} siteId - Site unique ID.
     * @param {string} variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getVariable(siteId: string, variableId: string): Promise<Models.Variable>;
    getVariable(
        paramsOrFirst: { siteId: string; variableId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Variable> {
        let params: { siteId: string; variableId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                variableId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                variableId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const variableId = params.variableId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/sites/{siteId}/variables/{variableId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace('{variableId}', encodeURIComponent(String(variableId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update variable by its unique ID.
     *
     * @param {string} params.siteId - Site unique ID.
     * @param {string} params.variableId - Variable unique ID.
     * @param {string | null} params.key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string | null} params.value - Variable value. Max length: 8192 chars.
     * @param {boolean | null} params.secret - Secret variables can be updated or deleted, but only sites can read them during build and runtime.
     * @param {string | null} params.description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     */
    updateVariable(params: {
        siteId: string;
        variableId: string;
        key?: string | null;
        value?: string | null;
        secret?: boolean | null;
        description?: string | null;
    }): Promise<Models.Variable>;
    /**
     * Update variable by its unique ID.
     *
     * @param {string} siteId - Site unique ID.
     * @param {string} variableId - Variable unique ID.
     * @param {string | null} key - Variable key. Letters, digits and underscores only, must not start with a digit. Max length: 255 chars.
     * @param {string | null} value - Variable value. Max length: 8192 chars.
     * @param {boolean | null} secret - Secret variables can be updated or deleted, but only sites can read them during build and runtime.
     * @param {string | null} description - Optional description for the variable.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Variable>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateVariable(
        siteId: string,
        variableId: string,
        key?: string | null,
        value?: string | null,
        secret?: boolean | null,
        description?: string | null,
    ): Promise<Models.Variable>;
    updateVariable(
        paramsOrFirst:
            | {
                  siteId: string;
                  variableId: string;
                  key?: string | null;
                  value?: string | null;
                  secret?: boolean | null;
                  description?: string | null;
              }
            | string,
        ...rest: [
            string?,
            (string | null)?,
            (string | null)?,
            (boolean | null)?,
            (string | null)?,
        ]
    ): Promise<Models.Variable> {
        let params: {
            siteId: string;
            variableId: string;
            key?: string | null;
            value?: string | null;
            secret?: boolean | null;
            description?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                variableId: string;
                key?: string | null;
                value?: string | null;
                secret?: boolean | null;
                description?: string | null;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                variableId: rest[0] as string,
                key: rest[1] as string | null,
                value: rest[2] as string | null,
                secret: rest[3] as boolean | null,
                description: rest[4] as string | null,
            };
        }

        const siteId = params.siteId;
        const variableId = params.variableId;
        const key = params.key;
        const value = params.value;
        const secret = params.secret;
        const description = params.description;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/sites/{siteId}/variables/{variableId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace('{variableId}', encodeURIComponent(String(variableId)));
        const apiPayload: Payload = {};
        if (typeof key !== 'undefined') {
            apiPayload['key'] = key;
        }
        if (typeof value !== 'undefined') {
            apiPayload['value'] = value;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof description !== 'undefined') {
            apiPayload['description'] = description;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'variable',
            models: {
                variable: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        key: '',
                        description: '',
                        value: '',
                        secret: false,
                        resourceType: '',
                        resourceId: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a variable by its unique ID.
     *
     * @param {string} params.siteId - Site unique ID.
     * @param {string} params.variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteVariable(params: { siteId: string; variableId: string }): Promise<{}>;
    /**
     * Delete a variable by its unique ID.
     *
     * @param {string} siteId - Site unique ID.
     * @param {string} variableId - Variable unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteVariable(siteId: string, variableId: string): Promise<{}>;
    deleteVariable(
        paramsOrFirst: { siteId: string; variableId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { siteId: string; variableId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                siteId: string;
                variableId: string;
            };
        } else {
            params = {
                siteId: paramsOrFirst as string,
                variableId: rest[0] as string,
            };
        }

        const siteId = params.siteId;
        const variableId = params.variableId;

        if (typeof siteId === 'undefined' || siteId === '') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        if (typeof variableId === 'undefined' || variableId === '') {
            throw new AppwriteException(
                'Missing required parameter: "variableId"',
            );
        }
        const apiPath = '/sites/{siteId}/variables/{variableId}'
            .replace('{siteId}', encodeURIComponent(String(siteId)))
            .replace('{variableId}', encodeURIComponent(String(variableId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }
}
